<template>
    <div
        class="flex flex-col gap-1"
        :class="[match?.isFixed ? 'flex-col-reverse' : 'flex-col']"
    >
        <!-- INTERSTATE -->
        <div
            v-if="session.isInterstate"
            class="flex items-center gap-2"
        >
            <Lozenge
                :class="{
                    'bg-muval-purple-2 text-muval-purple-1': match?.isPremium,
                    'bg-muval-gray-5 text-muval-gray-1': !match?.isPremium,
                }"
                >{{ match?.isPremium ? 'Premium' : 'Shared space' }}
                <Tippy
                    trigger="mouseenter click"
                    animation="scale"
                    placement="auto"
                >
                    <SvgIcon
                        name="system-information"
                        class="ml-1 h-4 w-4"
                    />
                    <template #content>
                        <div class="rounded-muval-2 shadow-muval-2 bg-white p-4 text-sm">
                            {{
                                match?.isPremium ?
                                    'Your own exclusive truck for a faster, more secure move.'
                                :   'Share a truck with other moves to save costs, with a flexible delivery window.'
                            }}
                        </div>
                    </template>
                </Tippy>
            </Lozenge>
        </div>
        <!-- LOCAL -->
        <div
            v-else
            class="flex items-center gap-2"
        >
            <template v-if="isBespokePricing">
                <Lozenge class="bg-muval-purple-2 text-muval-purple-1">Bespoke pricing </Lozenge>
            </template>
            <template v-else>
                <!-- Hidden when there is no label to sit beside it. A FIXED quote always has one
                     ("Cubic meter pricing"); an hourly quote only has one once a crew size was
                     recorded, and crew size is now nullable — so without this the icon is left
                     stranded on its own. -->
                <SvgIcon
                    v-if="match?.pricing_type === 'FIXED' || numberOfMovers"
                    :name="match?.pricing_type !== 'FIXED' ? 'system-two-people-outline' : 'system-deployed-code'"
                    class="h-5 w-5"
                />
                <!-- The pricing-model branch must stay keyed on pricing_type alone. Folding the
                     "no crew size recorded" case into this v-if would fall through to the v-else
                     and label an hourly quote as cubic-metre pricing. -->
                <template v-if="match?.pricing_type !== 'FIXED'">
                    <span
                        v-if="numberOfMovers"
                        class="text-sm"
                    >
                        {{ numberOfMovers }}
                    </span>
                </template>
                <span
                    v-else
                    class="text-sm"
                    >Cubic meter pricing</span
                >
            </template>
        </div>
        <!-- INTERSTATE -->
        <div
            v-if="session.isInterstate"
            class="flex items-center gap-2"
        >
            <SvgIcon
                name="system-truck"
                class="h-5 w-5"
            />
            <span class="text-sm"> {{ match?.isPremium ? 'Large truck' : 'Cost effective' }}</span>
        </div>
        <!-- LOCAL -->
        <div
            v-else-if="isBespokePricing || hasTruckInfo"
            class="flex items-center gap-2"
        >
            <template v-if="isBespokePricing">
                <SvgIcon
                    name="system-two-people-outline"
                    class="h-5 w-5"
                />
                <span class="text-sm">Qualified removalists</span>
            </template>
            <template v-else-if="hasTruckInfo">
                <SvgIcon
                    name="system-truck"
                    class="h-5 w-5"
                />
                <span class="text-sm">{{ formatTruckSize(match?.truck_size) }} ({{ spaceFormatted }})</span>
            </template>
        </div>
    </div>
</template>
<script setup lang="ts">
import SvgIcon from '@/components/utilities/SvgIcon.vue';
import { useSessionStore } from '@/store/session';
import type { MoveMatch } from '@/types';
import { computed } from 'vue';

const session = useSessionStore();

const formatTruckSize = (size?: string | null): string => {
    if (!size) {
        return 'Truck';
    }

    return size.toUpperCase().charAt(0) + size.toUpperCase().slice(1).toLowerCase().replace(/_/g, ' ') + ' truck';
};

interface Props {
    match?: MoveMatch | null;
    isFromRibbon?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    match: null,
    isFromRibbon: false,
});

const MOVER_COUNT_WORDS = ['One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];

/**
 * Crew size as a word, or null when none was recorded. Crew size is optional on the API, so
 * an absent value must render nothing rather than defaulting to a number nobody chose.
 */
const numberOfMovers = computed<string | null>(() => {
    const movers = props.match?.movers;

    if (typeof movers !== 'number' || movers < 1) {
        return null;
    }

    const word = MOVER_COUNT_WORDS[movers - 1] ?? String(movers);

    return `${word} ${movers === 1 ? 'removalist' : 'removalists'}`;
});

const spaceFormatted = computed(() => {
    const space = props.match?.truck_capacity;
    return space != null ? `${Math.round(space)}m³` : '0m³';
});

const hasTruckInfo = computed(() => {
    return props.match?.truck_size && props.match?.truck_capacity != null && props.match?.truck_capacity > 0;
});

const isBespokePricing = computed(() => {
    return props.match?.pricing_type === 'FIXED' && props.match?.match_type === 'CUSTOM';
});
</script>
